import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { destinationForClaims, readClaims } from '../../hooks/useSession';
import { isAllowedOtpType, safeRedirect } from '../../lib/authConfirmLink';
import { supabase } from '../../lib/supabase';

/**
 * Auth email links that stay on OUR domain.
 *
 * 🔴 WHY THIS EXISTS — a deliverability bug, not a feature request. Supabase's
 * default templates link to `<project>.supabase.co`, so a password-reset mail
 * arrived FROM `auctisync.com` carrying links to a completely different domain.
 * That From/link mismatch is a textbook phishing signature: on 2026-08-13 Gmail
 * accepted a reset mail (Resend logged it `delivered`) and then silently
 * discarded it — not inbox, not spam, not any tab, invisible to `in:anywhere`.
 * Nothing in the product could have told us; the only evidence was Resend's own
 * "ensure link URLs match sending domain" insight.
 *
 * Invite mail would have hit exactly the same wall, which would mean onboarding
 * failing at step zero for a paying customer with no error anywhere.
 *
 * The fix is Supabase's `{{ .TokenHash }}` template variable: the email links
 * here instead, and this page exchanges the hash for a session client-side.
 * Supabase also SELLS a fix (Custom Domains, ~$10/mo) — not taken, because this
 * is free and we own it.
 *
 * URL shape:
 *   /auth/confirm?token_hash=<hash>&type=recovery&redirect_to=/auth/reset-password
 *
 * DIFFERENT MECHANISM FROM AuthCallback, deliberately kept separate. That page
 * handles links carrying a URL *hash* which the client auto-consumes
 * (`detectSessionInUrl`), so it waits and polls. This one is handed a token in
 * the *query string* and must redeem it explicitly. Folding them together would
 * mean one component with two unrelated control flows.
 */

export function AuthConfirm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  // Staff and bidder links share this route, so a failure must return the user
  // to the sign-in that matches THEIR audience — same reasoning as AuthCallback.
  const nextParam = safeRedirect(searchParams.get('redirect_to')) ?? '';
  const backToSignIn =
    nextParam.startsWith('/admin') || nextParam.startsWith('/super') ? '/admin' : '/signin';

  useEffect(() => {
    const tokenHash = searchParams.get('token_hash');
    const rawType = searchParams.get('type');
    const redirectTo = safeRedirect(searchParams.get('redirect_to'));

    if (tokenHash === null || tokenHash === '' || rawType === null) {
      setError('This link is missing information. Request a new one.');
      return;
    }
    if (!isAllowedOtpType(rawType)) {
      setError('This link type is not supported. Request a new one.');
      return;
    }
    const type = rawType;

    let cancelled = false;

    async function confirm() {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        token_hash: tokenHash as string,
        type,
      });
      if (cancelled) return;

      if (verifyError) {
        // Supabase's own wording here ("Token has expired or is invalid") is
        // already the plain-English answer, so it is surfaced rather than
        // replaced with something vaguer.
        setError(verifyError.message);
        return;
      }
      if (!data.session) {
        setError('That link could not be verified. It may have expired or already been used.');
        return;
      }

      // Recovery and invite both OVERRIDE redirect_to, for the same reason:
      // neither user has a usable password yet.
      //
      // Recovery — sending them onward leaves an account whose credentials the
      // person holding the email still controls.
      //
      // Invite — Supabase creates the account WITHOUT a password. The link signs
      // them in, so skipping ahead looks like it worked until they close the tab
      // and find nothing to sign in with. Overriding here (rather than trusting
      // the email template's redirect_to) means an invite cannot be aimed past
      // the set-password step even if a template is edited later.
      const destination =
        type === 'recovery'
          ? '/auth/reset-password'
          : type === 'invite'
            ? '/auth/accept-invite'
            : (redirectTo ?? destinationForClaims(readClaims(data.session)));
      await navigate(destination, { replace: true });
    }

    void confirm();
    return () => {
      cancelled = true;
    };
  }, [navigate, searchParams]);

  return (
    <main
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      {error !== null ? (
        <div style={{ maxWidth: '32rem' }}>
          <h1
            style={{
              fontFamily: 'var(--font-heading-active)',
              fontSize: '1.5rem',
              fontWeight: 700,
              color: 'var(--text)',
              marginBottom: '0.5rem',
            }}
          >
            Link didn&apos;t work
          </h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>{error}</p>
          <a href={backToSignIn} style={{ color: 'var(--brand-primary)' }}>
            Back to sign-in
          </a>
        </div>
      ) : (
        <p style={{ color: 'var(--text-muted)' }}>Confirming…</p>
      )}
    </main>
  );
}
